/* <cart-drawer>: the cart as a drawer, ported from docs/design/maqueta.html (v8, lines 842-870).
   product-form.js adds to the cart and hands the section HTML to render(); "Remove" posts to
   /cart/change.js and does the same. The shipping country is display only: it picks the shipping
   note and the currency of the prices (a later choice in the footer picker wins until the next country
   change); it is kept in jc-country. */
import { currencyOf, currentCurrency } from "currency";

const store = {
  get: key => { try { return localStorage.getItem(key); } catch { return null; } },
  set: (key, value) => { try { localStorage.setItem(key, value); } catch {} },
};

class CartDrawer extends HTMLElement {
  connectedCallback() {
    // connectedCallback runs again if the element is moved in the DOM; bind the listeners once,
    // and only the document-level ones again (disconnectedCallback removed them)
    if (this.ready) {
      document.addEventListener("keydown", this.onKey);
      document.addEventListener("click", this.onDocClick);
      return;
    }
    this.ready = true;
    this.drawer = this.querySelector(".drawer");
    this.scrim = this.querySelector("[data-scrim]");
    this.country = this.querySelector("#country");
    this.status = this.querySelector("[data-status]");
    this.lang = document.documentElement.lang.startsWith("en") ? "en" : "es";

    this.querySelector("[data-close]").addEventListener("click", () => this.close());
    this.scrim.addEventListener("click", () => this.close());
    this.addEventListener("click", e => {
      const remove = e.target.closest("[data-key]");
      if (remove) this.remove(remove);
    });
    this.country.addEventListener("change", () => {
      store.set("jc-country", this.country.value);
      // as in the mockup, picking a country switches the currency (pesos if it has no rate: currentCurrency() checks)
      store.set("jc-currency", currencyOf(this.country.value));
      // sync the footer picker with the currency the prices now use, then repaint them
      document.querySelectorAll(".foot .curSel").forEach(s => { s.value = currentCurrency(); s.closest("jc-select")?.sync?.(); });
      document.dispatchEvent(new Event("jc:prices"));
      this.paintNote();
    });
    // the footer's currency picker changes the charge notice too
    document.addEventListener("change", e => { if (e.target.matches?.(".foot .curSel")) this.paintNote(); });

    this.onKey = e => {
      if (!this.isOpen) return;
      if (e.key === "Escape") this.close();
      else if (e.key === "Tab") this.trapTab(e);
    };
    // the header's "Carrito" stays a link to /cart; with JS it opens the drawer (not on /cart itself)
    this.onDocClick = e => {
      const link = e.target.closest?.("a.cartbtn");
      if (!link || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      if (document.querySelector(".cart-page")) return;
      e.preventDefault();
      this.open();
    };
    document.addEventListener("keydown", this.onKey);
    document.addEventListener("click", this.onDocClick);

    this.paintCountries();
    this.paintNote();
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.onKey);
    document.removeEventListener("click", this.onDocClick);
  }

  get isOpen() { return this.drawer.classList.contains("open"); }

  open() {
    this.opener = document.activeElement;
    this.drawer.inert = false;
    this.drawer.classList.add("open");
    this.scrim.classList.add("open");
    this.drawer.setAttribute("aria-hidden", "false");
    this.querySelector("[data-close]").focus();
  }

  close() {
    this.drawer.classList.remove("open");
    this.scrim.classList.remove("open");
    this.drawer.setAttribute("aria-hidden", "true");
    this.drawer.inert = true;
    this.status.textContent = "";
    if (this.opener?.isConnected) this.opener.focus();
    this.opener = null;
  }

  // Tab and Shift+Tab cycle inside the open drawer
  trapTab(e) {
    const items = [...this.drawer.querySelectorAll("a[href], button:not([disabled]), select:not([aria-hidden])")].filter(el => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* Swap in the lines, the subtotal, the button state and the counts from the section HTML the
     Section Rendering API returned ({ sections: { "cart-drawer": html } }). */
  render(sectionHtml) {
    const next = new DOMParser().parseFromString(sectionHtml, "text/html").querySelector("cart-drawer");
    if (!next) return;
    this.querySelector("[data-lines]").innerHTML = next.querySelector("[data-lines]").innerHTML;
    this.querySelector("[data-subtotal]").innerHTML = next.querySelector("[data-subtotal]").innerHTML;
    const checkout = this.querySelector('button[name="checkout"]');
    checkout.disabled = next.querySelector('button[name="checkout"]').disabled;
    const count = next.dataset.count;
    this.dataset.count = count;
    this.querySelectorAll("[data-cart-count]").forEach(el => { el.textContent = count; });
    document.querySelectorAll(".count-dot").forEach(el => { el.dataset.n = count; el.textContent = count; });
    document.dispatchEvent(new Event("jc:prices"));
  }

  // Removes by line key, not index: the key stays right if the cart changed in another tab meanwhile
  async remove(button) {
    // the re-render replaces the lines, so focus would drop to the page: remember if it was in them
    const hadFocus = this.querySelector("[data-lines]").contains(document.activeElement) || document.activeElement === button;
    button.disabled = true;
    this.status.textContent = "";
    try {
      let res, data;
      try {
        res = await fetch(this.dataset.changeUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: button.dataset.key, quantity: 0, sections: "cart-drawer" }),
        });
        data = await res.json();
      } catch {
        throw new Error(this.dataset.error); // offline, or a reply that is not JSON
      }
      // Shopify explains refusals in "description"
      if (!res.ok) throw new Error(data.description || this.dataset.error);
      if (!data.sections?.["cart-drawer"]) throw new Error(this.dataset.error);
      this.render(data.sections["cart-drawer"]);
      if (hadFocus) this.querySelector("[data-close]").focus();
    } catch (err) {
      button.disabled = false;
      this.status.textContent = err.message || this.dataset.error;
    }
  }

  // Uruguay first, the rest by name in the page language; each option shows its display currency
  paintCountries() {
    const options = [...this.country.options];
    options.sort((a, b) => (a.value === "UY" ? -1 : b.value === "UY" ? 1 : a.dataset.name.localeCompare(b.dataset.name, this.lang)));
    options.forEach(o => { o.textContent = `${o.dataset.name} (${currencyOf(o.value)})`; this.country.append(o); });
    const saved = store.get("jc-country");
    const initial = [saved, this.dataset.here].find(code => code && options.some(o => o.value === code));
    if (initial) this.country.value = initial;
    this.country.closest("jc-select")?.sync?.();
  }

  paintNote() {
    const note = this.querySelector("[data-ship-note]");
    const code = currentCurrency();
    const ship = this.country.value === "UY" ? this.dataset.shipUy : this.dataset.shipIntl;
    note.textContent = code === "UYU" ? ship : `${ship} ${this.dataset.charge.replace("{c}", code)}`;
  }
}
if (!customElements.get("cart-drawer")) customElements.define("cart-drawer", CartDrawer);
